import { flow, getRoot } from 'mobx-state-tree'
import { initializeCheckout, ensureCheckoutWebComponents } from '~/utilities/checkout'
import { checkoutAppearance, ACCEPTED_CARD_SCHEMES } from '~/style/theme/checkoutAppearance'
import {
  createPaymentSession,
  createTrafficPaymentSession,
} from '~/services/payments/paymentSessions'
import * as PaymentService from '~/services/payments'
import { PAYMENT_PROVIDERS } from '~/utilities/constants'

function parseCustomerPhone(phoneNumber) {
  if (!phoneNumber) return undefined
  if (phoneNumber.startsWith('+1')) {
    return { country_code: '+1', number: phoneNumber.slice(2) }
  }
  if (phoneNumber.startsWith('+')) {
    const match = phoneNumber.match(/^(\+\d{1,3})(.+)$/)
    if (match) {
      return { country_code: match[1], number: match[2] }
    }
  }
  return { country_code: '+1', number: phoneNumber }
}

function buildCustomerData(user) {
  if (!user?.email) return undefined
  const phone = parseCustomerPhone(user.phoneNumber)
  return {
    email: user.email,
    ...(user.firstName && user.lastName && { name: `${user.firstName} ${user.lastName}` }),
    ...(phone && { phone }),
  }
}

function normalizeCardScheme(scheme) {
  if (!scheme) return undefined
  const normalized = String(scheme).toLowerCase().replace(/\s+/g, '_')
  if (normalized === 'american_express') return 'amex'
  return normalized
}

function extractCardScheme(tokenizeResult, submitData) {
  const raw =
    tokenizeResult?.cardMetadata?.scheme ??
    tokenizeResult?.data?.scheme ??
    submitData?.cardMetadata?.scheme ??
    submitData?.scheme ??
    submitData?.data?.scheme
  return normalizeCardScheme(raw)
}

async function extractPaymentSubmission(component, submitData) {
  if (component.tokenize) {
    const tokenizeResult = await component.tokenize()
    const token = tokenizeResult?.data?.token
    if (!token) {
      throw new Error('Tokenize result missing token')
    }
    return {
      token,
      cardScheme: extractCardScheme(tokenizeResult, submitData),
      last4: tokenizeResult?.cardMetadata?.last4 ?? tokenizeResult?.data?.last4,
    }
  }
  if (submitData.session_data && typeof submitData.session_data === 'string') {
    return {
      token: submitData.session_data,
      cardScheme: extractCardScheme(null, submitData),
    }
  }
  if (submitData.token && typeof submitData.token === 'string') {
    return {
      token: submitData.token,
      cardScheme: extractCardScheme(null, submitData),
    }
  }
  return { token: null, cardScheme: undefined }
}

function createFlowMountOptions(self, handleSubmit, createAddressComponent = true) {
  return {
    paymentSession: self._flowPaymentSessionData,
    environment: process.env.CKO_ENVIRONMENT || 'sandbox',
    containerId: self._flowContainerId || 'flow-container',
    addressContainerId: createAddressComponent ? 'billing-address-container' : undefined,
    appearance: checkoutAppearance,
    componentOptions: {
      data: { billingCountry: 'US' },
      flow: { acceptedCardSchemes: ACCEPTED_CARD_SCHEMES },
    },
    handleSubmit,
    createAddressComponent,
    flowComponentOptions: {
      onChange: component => {
        self.setFlowComponentValid(component.isValid())
      },
      onReady: component => {
        self.setFlowComponentReady(component.isValid())
      },
      onCardBinChanged: (_component, cardMetadata) => {
        const cardScheme = normalizeCardScheme(cardMetadata?.scheme)
        if (cardScheme) {
          self.setFlowCardScheme(cardScheme, cardMetadata?.last4)
        }
      },
      onTokenized: (_component, tokenizeResult) => {
        const cardScheme = extractCardScheme(tokenizeResult, null)
        const last4 = tokenizeResult?.cardMetadata?.last4 ?? tokenizeResult?.data?.last4
        if (cardScheme) {
          self.setFlowCardScheme(cardScheme, last4)
        }
        return { continue: true }
      },
    },
  }
}

export function createPaymentFlowActions(self) {
  return {
    setFlowComponentValid(isValid) {
      self.cko.isValid = isValid
    },

    setFlowComponentReady(isValid) {
      self.cko.isReady = true
      self.cko.isValid = isValid
    },

    setFlowLoading(isLoading) {
      self._isLoading = isLoading
    },

    prepareFlowPayment(paymentToken, cardScheme, last4) {
      self.cko.token = paymentToken
      if (cardScheme) {
        self.cko.cardScheme = cardScheme
      }
      if (last4) {
        self.cko.last4 = last4
      }
      self.isAddingNewPaymentMethod = true
    },

    setFlowCardScheme(cardScheme, last4) {
      if (cardScheme) {
        self.cko.cardScheme = cardScheme
      }
      if (last4) {
        self.cko.last4 = last4
      }
    },

    completeDashboardFlowSave(updatedMethods) {
      self.cko.setSavedCards(updatedMethods)
      self._dashboardSaveComplete = true
    },

    setCkoSavedCards(cards) {
      self.cko.setSavedCards(cards)
    },

    remountFlow() {
      if (self.flowComponent && typeof self.flowComponent.unmount === 'function') {
        try {
          self.flowComponent.unmount()
        } catch (error) {
          console.error('Error unmounting flow component:', error)
        }
      }
      self.flowComponent = null
      self.checkoutInstance = null
      self._flowPaymentSessionData = null
      self._flowSessionInitialized = false

      const containerIds = ['flow-container', 'dashboard-flow-container']
      containerIds.forEach(id => {
        const el = document.getElementById(id)
        if (el) {
          el.innerHTML = ''
          el.removeAttribute('data-cko-mounted')
        }
      })
      self.cko.teardown()
    },

    submitPayment: flow(function* submitPaymentGen() {
      if (!self.flowComponent) {
        throw new Error('Flow component is not initialized')
      }
      const flowComponent = self.flowComponent
      if (flowComponent.submit) {
        const submitResult = flowComponent.submit()
        if (submitResult && typeof submitResult.then === 'function') {
          yield submitResult
        }
        return true
      }
      throw new Error('Flow component does not support submit method')
    }),

    initializePaymentSession: flow(function* initializePaymentSessionGen(
      totalAmount,
      ticketIds,
      flowType = 'pay',
    ) {
      if (
        self._flowSessionInitialized ||
        self._isFlowInitializing ||
        ((flowType === 'pay' || flowType === 'robotpay') && totalAmount <= 0)
      ) {
        return false
      }

      try {
        if (self.flowComponent && typeof self.flowComponent.unmount === 'function') {
          self.flowComponent.unmount()
        }

        self._isFlowInitializing = true
        self._isLoading = true
        self._flowTicketIds = ticketIds
        self._flowType = flowType
        self._flowContainerId = 'flow-container'

        const rootStore = getRoot(self)
        const { user } = rootStore.auth
        const customer = buildCustomerData(user)

        const sessionFlowType = flowType === 'fight' ? 'fight' : 'pay'
        const responseData = yield createPaymentSession({
          amount: flowType === 'fight' ? 0 : Math.round(totalAmount * 100),
          currency: 'USD',
          flowType: sessionFlowType,
          reference: `PARKING-${ticketIds.join('-')}`,
          display_name: flowType === 'fight' ? 'WinIt Card Verification' : 'WinIt Parking Tickets',
          billing: { address: { country: 'US' } },
          customer,
          success_url: `${window.location.origin}/parking/success`,
          failure_url: `${window.location.origin}/parking/flow`,
        })

        const { data } = responseData
        if (!data?.payment_session_token) {
          throw new Error('Invalid payment session response')
        }

        yield ensureCheckoutWebComponents()
        self._flowPaymentSessionData = data

        const handleSubmit = async (component, submitData) => {
          if (!component.isValid() || submitData.isValid === false) {
            self.showErrorToast('Please fill in all required payment fields correctly')
            self.setFlowLoading(false)
            return
          }

          let paymentToken
          let cardScheme
          let last4
          try {
            const submission = await extractPaymentSubmission(component, submitData)
            paymentToken = submission.token
            cardScheme = submission.cardScheme
            last4 = submission.last4
          } catch (error) {
            console.error('Error tokenizing payment:', error)
            self.showErrorToast(
              'Failed to tokenize payment details. Please check your payment information and try again.',
            )
            self.setFlowLoading(false)
            return
          }

          if (!paymentToken) {
            self.showErrorToast('Payment token is missing. Please try again.')
            self.setFlowLoading(false)
            return
          }

          try {
            self.prepareFlowPayment(
              paymentToken,
              cardScheme || self.cko.cardScheme,
              last4 || self.cko.last4,
            )
            await self.updatePaymentMethod()
            await self.checkoutFunction(paymentToken)
          } catch (err) {
            console.error('Error processing payment:', err)
            self.showErrorToast(err.message || 'Failed to process payment')
            self.remountFlow()
            throw err
          } finally {
            self.setFlowLoading(false)
          }
        }

        const checkoutResult = yield initializeCheckout(
          createFlowMountOptions(self, handleSubmit, true),
        )

        if (!checkoutResult) {
          // Mount point wasn't on screen; abort without surfacing an error so
          // the session can be re-initialized once the container is rendered.
          return false
        }

        self.checkoutInstance = checkoutResult.checkout
        self.flowComponent = checkoutResult.flowComponent
        self._flowSessionInitialized = true
        self._flowPaymentSessionToken = data.payment_session_token
        return true
      } catch (err) {
        console.error('Error initializing payment session:', err)
        self.showErrorToast(err.message || 'Failed to initialize payment')
        return false
      } finally {
        self._isFlowInitializing = false
        self._isLoading = false
      }
    }),

    initializeTrafficPaymentSession: flow(function* initializeTrafficPaymentSessionGen(
      totalAmount,
      ticketObjectId,
    ) {
      if (self._flowSessionInitialized || self._isFlowInitializing || totalAmount <= 0) {
        return false
      }

      try {
        if (self.flowComponent && typeof self.flowComponent.unmount === 'function') {
          self.flowComponent.unmount()
        }

        self._isFlowInitializing = true
        self._isLoading = true
        self._flowTicketIds = [ticketObjectId]
        self._flowType = 'pay'
        self._flowContainerId = 'flow-container'

        const rootStore = getRoot(self)
        const { user } = rootStore.auth
        const customer = buildCustomerData(user)

        const responseData = yield createTrafficPaymentSession({
          amount: Math.round(totalAmount * 100),
          currency: 'USD',
          flowType: 'pay',
          reference: `TRAFFIC-${ticketObjectId}`,
          display_name: 'WinIt Traffic Ticket',
          billing: { address: { country: 'US' } },
          customer,
          success_url: `${window.location.origin}/traffic-tickets/flow`,
          failure_url: `${window.location.origin}/traffic-tickets/flow`,
        })

        const { data } = responseData
        if (!data?.payment_session_token) {
          throw new Error('Invalid payment session response')
        }

        yield ensureCheckoutWebComponents()
        self._flowPaymentSessionData = data

        const handleSubmit = async (component, submitData) => {
          if (!component.isValid() || submitData.isValid === false) {
            self.showErrorToast('Please fill in all required payment fields correctly')
            self.setFlowLoading(false)
            return
          }

          let paymentToken
          let cardScheme
          let last4
          try {
            const submission = await extractPaymentSubmission(component, submitData)
            paymentToken = submission.token
            cardScheme = submission.cardScheme
            last4 = submission.last4
          } catch (error) {
            console.error('Error tokenizing payment:', error)
            self.showErrorToast(
              'Failed to tokenize payment details. Please check your payment information and try again.',
            )
            self.setFlowLoading(false)
            return
          }

          if (!paymentToken) {
            self.showErrorToast('Payment token is missing. Please try again.')
            self.setFlowLoading(false)
            return
          }

          try {
            self.prepareFlowPayment(
              paymentToken,
              cardScheme || self.cko.cardScheme,
              last4 || self.cko.last4,
            )
            await self.updatePaymentMethod()
            await self.checkoutFunction()
          } catch (err) {
            console.error('Error processing traffic payment:', err)
            self.showErrorToast(err.message || 'Failed to process payment')
            self.remountFlow()
            throw err
          } finally {
            self.setFlowLoading(false)
          }
        }

        const checkoutResult = yield initializeCheckout(
          createFlowMountOptions(self, handleSubmit, true),
        )

        if (!checkoutResult) {
          return false
        }

        self.checkoutInstance = checkoutResult.checkout
        self.flowComponent = checkoutResult.flowComponent
        self._flowSessionInitialized = true
        self._flowPaymentSessionToken = data.payment_session_token
        return true
      } catch (err) {
        console.error('Error initializing traffic payment session:', err)
        self.showErrorToast(err.message || 'Failed to initialize payment')
        return false
      } finally {
        self._isFlowInitializing = false
        self._isLoading = false
      }
    }),

    initializeDashboardPaymentSession: flow(function* initializeDashboardPaymentSessionGen() {
      if (self._flowSessionInitialized || self._isFlowInitializing) {
        return false
      }

      try {
        if (self.flowComponent && typeof self.flowComponent.unmount === 'function') {
          self.flowComponent.unmount()
        }

        self._isFlowInitializing = true
        self._isLoading = true
        self._flowContainerId = 'dashboard-flow-container'

        const rootStore = getRoot(self)
        const { user } = rootStore.auth
        const customerEmail = user?.email

        // CKO Flow only surfaces a customer's stored cards when the session is
        // tied to their CKO customer id (cus_...). Passing only the email creates
        // a fresh/unlinked customer, so saved cards never appear on the dashboard.
        const ckoCustomerId =
          typeof self.customerId === 'string' && self.customerId.startsWith('cus_')
            ? self.customerId
            : undefined
        let customer
        if (ckoCustomerId) {
          customer = { id: ckoCustomerId }
        } else if (customerEmail) {
          customer = { email: customerEmail }
        }

        const sessionPayload = {
          amount: 0,
          currency: 'USD',
          flowType: 'fight',
          reference: 'DASHBOARD-PM',
          display_name: 'WinIt Saved Payment Method',
          billing: { address: { country: 'US' } },
          customer,
          success_url: `${window.location.origin}/dashboard/payment-method`,
          failure_url: `${window.location.origin}/dashboard/payment-method`,
        }

        const responseData = yield createPaymentSession(sessionPayload)
        const { data } = responseData
        if (!data?.payment_session_token) {
          throw new Error('Invalid payment session response')
        }

        yield ensureCheckoutWebComponents()
        self._flowPaymentSessionData = data

        const handleSubmit = async (component, submitData) => {
          if (!component.isValid() || submitData.isValid === false) {
            self.showErrorToast('Please fill in all required payment fields correctly')
            self.setFlowLoading(false)
            return
          }

          let paymentToken
          let cardScheme
          let last4
          try {
            const submission = await extractPaymentSubmission(component, submitData)
            paymentToken = submission.token
            cardScheme = submission.cardScheme
            last4 = submission.last4
          } catch (error) {
            self.showErrorToast(
              'Failed to tokenize payment details. Please check your payment information and try again.',
            )
            self.setFlowLoading(false)
            return
          }

          if (!paymentToken) {
            self.showErrorToast('Payment token is missing. Please try again.')
            self.setFlowLoading(false)
            return
          }

          try {
            self.setFlowLoading(true)
            self.prepareFlowPayment(
              paymentToken,
              cardScheme || self.cko.cardScheme,
              last4 || self.cko.last4,
            )

            const syncResponse = await PaymentService.updateDefaultPaymentMethodForCko(
              paymentToken,
              rootStore.auth.user.sessionToken,
              rootStore.auth.user.email,
            )
            if (syncResponse !== '') {
              throw new Error(syncResponse)
            }

            try {
              const updatedMethods = await PaymentService.getAllPaymentMethods(
                rootStore.auth.user.sessionToken,
              )
              self.completeDashboardFlowSave(updatedMethods)
            } catch (fetchErr) {
              console.error('Error refreshing saved cards:', fetchErr)
            }
          } catch (err) {
            self.showErrorToast(err.message || 'Failed to save payment method')
            throw err
          } finally {
            self.setFlowLoading(false)
          }
        }

        const checkoutResult = yield initializeCheckout(
          createFlowMountOptions(self, handleSubmit, false),
        )

        if (!checkoutResult) {
          return false
        }

        self.checkoutInstance = checkoutResult.checkout
        self.flowComponent = checkoutResult.flowComponent
        self._flowSessionInitialized = true
        return true
      } catch (err) {
        console.error('Error initializing dashboard payment session:', err)
        self.showErrorToast(err.message || 'Failed to initialize payment form')
        return false
      } finally {
        self._isFlowInitializing = false
        self._isLoading = false
      }
    }),

    teardownFlow() {
      if (
        self.flowComponent &&
        typeof self.flowComponent.unmount === 'function'
      ) {
        try {
          self.flowComponent.unmount()
        } catch (error) {
          console.error('Error unmounting flow component:', error)
        }
      }

      ;['flow-container', 'dashboard-flow-container', 'billing-address-container'].forEach(id => {
        const el = document.getElementById(id)
        if (el) {
          el.innerHTML = ''
          el.removeAttribute('data-cko-mounted')
        }
      })

      self.checkoutInstance = null
      self.flowComponent = null
      self._flowPaymentSessionData = null
      self._flowPaymentSessionToken = ''
      self._flowSessionInitialized = false
      self._isFlowInitializing = false
      self._flowTicketIds = []
      self._dashboardSaveComplete = false
    },

    checkoutWithFlow: flow(function* checkoutWithFlowGen() {
      const success = yield self.checkout({ useFlowSubmit: true })
      if (success === false) return false
      return yield self.submitPayment()
    }),
  }
}

export function usesCkoFlow(paymentProcessor) {
  return (
    paymentProcessor === PAYMENT_PROVIDERS.cko ||
    (paymentProcessor === PAYMENT_PROVIDERS.evervault &&
      process.env.ENABLE_CKO_PAYMENTS === 'true')
  )
}

/**
 * Whether to mount the CKO Flow drop-in (vs native Evervault card form on parking pay).
 * Evervault + ENABLE_CKO uses CKO Flow for fight/traffic only; pay flows use EvervaultCard.
 */
export function showCkoFlowDropin(
  paymentProcessor,
  {
    isFightFlow = false,
    isTrafficFlow = false,
    isPayFlow = false,
    isRobotPayFlow = false,
    isPayFlowFromMobile = false,
    isDashboardPayment = false,
  } = {},
) {
  if (paymentProcessor === PAYMENT_PROVIDERS.cko) {
    return (
      isFightFlow ||
      isTrafficFlow ||
      isPayFlow ||
      isRobotPayFlow ||
      isPayFlowFromMobile ||
      isDashboardPayment
    )
  }

  if (
    paymentProcessor === PAYMENT_PROVIDERS.evervault &&
    process.env.ENABLE_CKO_PAYMENTS === 'true'
  ) {
    return isFightFlow || isTrafficFlow
  }

  return false
}
